import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { integrationsApi } from "../api/integrations.api";
import type { IntegrationModule, ModuleConfiguration } from "../types";
import { runModuleToggleHandlers } from "./moduleToggleHandlers";
import { integrationModuleKeys } from "./useIntegrationModule";
import { integrationModulesKeys } from "./useIntegrationModules";
import { integrationsKeys } from "./useIntegrations";
import { useUiSchema } from "./useUiSchema";

import useToast from "@/lib/hooks/useToast";
import type { ConfigurationContext } from "@/types/configuration";

// Query key factory for module configuration
export const moduleConfigurationKeys = {
  all: ["moduleConfiguration"] as const,
  schemas: () => [...moduleConfigurationKeys.all, "schema"] as const,
  schema: (
    integrationName: string,
    moduleName: string,
    context: ConfigurationContext,
  ) =>
    [
      ...moduleConfigurationKeys.schemas(),
      integrationName,
      moduleName,
      context,
    ] as const,
  values: () => [...moduleConfigurationKeys.all, "values"] as const,
  value: (
    integrationName: string,
    moduleName: string,
    context: ConfigurationContext,
  ) =>
    [
      ...moduleConfigurationKeys.values(),
      integrationName,
      moduleName,
      context,
    ] as const,
};

// Hook to fetch module configuration schema — delegates to the unified useUiSchema hook.
// Kept for backward compatibility: existing callers (ClientReportingPage, useSchemaBasedProvider, etc.)
// continue to work unchanged.
export function useModuleConfigurationSchema(
  integrationName: string,
  moduleName: string,
  context: ConfigurationContext,
  enabled = true,
) {
  return useUiSchema(
    { type: "module", integrationName, moduleName },
    context,
    enabled,
  );
}

// Hook to fetch current module configuration
export function useModuleConfiguration(
  integrationName: string,
  moduleName: string,
  context: ConfigurationContext,
  enabled = true,
) {
  return useQuery({
    queryKey: moduleConfigurationKeys.value(
      integrationName,
      moduleName,
      context,
    ),
    queryFn: () =>
      integrationsApi.fetchModuleConfiguration(
        integrationName,
        moduleName,
        context,
      ),
    staleTime: 20 * 60 * 1000, // 20 minutes
    enabled,
  });
}

export type UseUpdateModuleConfigurationOptions = {
  onSuccessClose?: () => void;
};

// Hook to update module configuration
export function useUpdateModuleConfiguration(
  context: ConfigurationContext,
  options?: UseUpdateModuleConfigurationOptions,
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const onSuccessClose = options?.onSuccessClose;

  return useMutation({
    mutationFn: async ({
      integrationName,
      moduleName,
      configurations,
      isActive,
      currentIsActive: _currentIsActive,
    }: {
      integrationName: string;
      moduleName: string;
      configurations: ModuleConfiguration;
      isActive: boolean;
      currentIsActive: boolean;
    }) => {
      // Use atomic update endpoint to ensure both operations succeed or fail together
      await integrationsApi.updateIntegrationModuleAtomic(
        integrationName,
        moduleName,
        context,
        configurations,
        isActive,
      );
    },
    onError: (err: unknown) => {
      // Extract error from response - handle ProblemDetails format (title/detail) and legacy format (error/message)
      const error = err as {
        response?: {
          data?: {
            title?: string;
            detail?: string;
            error?: string;
            message?: string;
          };
        };
        message?: string;
      };

      const errorData = error?.response?.data;
      const errorCode = errorData?.title || errorData?.error;
      const errorDetail = errorData?.detail || errorData?.message;

      // Try to translate the error code generically
      if (errorCode) {
        // Convert error code to camelCase format for translation keys
        // e.g., "Integration.Module.HasActiveClients" -> "hasActiveClients"
        const convertToCamelCase = (str: string): string => {
          const parts = str.split(".");
          const lastPart = parts[parts.length - 1] || str;
          return lastPart.charAt(0).toLowerCase() + lastPart.slice(1);
        };

        const errorCodeKey = convertToCamelCase(errorCode);

        // Try integrations.modules.configure.errors first (for module-specific errors)
        const moduleErrorKey = `integrations.modules.configure.errors.${errorCodeKey}`;
        const moduleErrorTranslation = t(moduleErrorKey, {
          defaultValue: null,
        });
        if (
          moduleErrorTranslation !== null &&
          moduleErrorTranslation !== moduleErrorKey
        ) {
          // Extract count from detail message if available (e.g., "Cannot deactivate: 1 client(s) have this module active")
          const countMatch = errorDetail?.match(/(\d+)\s+client/i);
          const count = countMatch ? Number.parseInt(countMatch[1], 10) : 1;

          const translatedMessage = t(moduleErrorKey, { count });
          showToast(translatedMessage, "error");
          return;
        }

        // Try errorCodes (for general error codes) - use original format or camelCase
        const generalErrorKey = `errorCodes.${errorCode}`;
        const generalErrorTranslation = t(generalErrorKey, {
          defaultValue: null,
        });
        if (
          generalErrorTranslation !== null &&
          generalErrorTranslation !== generalErrorKey
        ) {
          const translatedMessage = t(generalErrorKey);
          showToast(translatedMessage, "error");
          return;
        }
      }

      // Fallback to detail/message or default
      const errorMessage =
        errorDetail ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        error?.message ||
        "Failed to update module configuration";

      // Show error toast
      showToast(errorMessage, "error");
    },
    onSuccess: (_data, variables) => {
      // Update cache immediately after success (before invalidation refetch)
      // This makes the UI update faster than waiting for the refetch
      const {
        integrationName,
        moduleName,
        configurations,
        isActive,
        currentIsActive,
      } = variables;

      // Update module list in cache - only update the cache that matches the current context
      let shouldDisableParentIntegration = false;

      // Build query params based on context to only update matching cache entries
      const moduleQueryParams =
        context.level === "client"
          ? {
            integrationName,
            tenantId: context.tenantId,
            clientId: context.clientId,
          }
          : { integrationName, tenantId: context.tenantId, clientId: null };

      let moduleNameForDetail: string | null = null;

      queryClient.setQueriesData(
        { queryKey: integrationModulesKeys.list(moduleQueryParams) },
        (old: unknown) => {
          if (!old || !Array.isArray(old)) return old;

          const updatedModules = old.map((module: unknown) => {
            const mod = module as IntegrationModule;
            if (mod.moduleName === moduleName) {
              moduleNameForDetail = mod.moduleName; // Extract module name for detail cache update
              return {
                ...mod,
                isActive,
                activatedDate: isActive ? new Date().toISOString() : undefined,
              };
            }
            return module;
          });

          // Check if this is the last active module being disabled
          if (!isActive && currentIsActive) {
            const activeCount = updatedModules.filter((m: unknown) => {
              const mod = m as IntegrationModule;
              return mod.isActive && mod.moduleName !== moduleName;
            }).length;
            shouldDisableParentIntegration = activeCount === 0;
          }

          return updatedModules;
        },
      );

      // Also update the detail query cache if we have module data
      // This ensures the detail page (e.g., JobSyncClientDetailPage) sees the updated status immediately
      // and can redirect properly when module is disabled
      if (moduleNameForDetail) {
        // Update all detail queries for this module (matching by module name pattern)
        // We use a pattern match since we don't know the exact integrationName, but we can match by moduleName
        queryClient.setQueriesData(
          {
            queryKey: integrationModuleKeys.all,
            predicate: (query) => {
              const key = query.queryKey;
              // Match detail queries: ['integrationModule', integrationName, moduleName, tenantId?, clientId?]
              // We check if it's a detail query (has 4+ parts) and matches our context
              if (
                Array.isArray(key) &&
                key.length >= 4 &&
                key[0] === "integrationModule"
              ) {
                const queryModuleName = key[2];
                const queryTenantId = key[3];
                const queryClientId = key[4];

                // Match module name and context
                const moduleMatches = queryModuleName === moduleNameForDetail;
                const contextMatches =
                  context.level === "client"
                    ? queryTenantId === context.tenantId &&
                      queryClientId === context.clientId
                    : queryTenantId === context.tenantId &&
                      (queryClientId === undefined || queryClientId === null);

                return moduleMatches && contextMatches;
              }
              return false;
            },
          },
          (old: unknown) => {
            if (!old || typeof old !== "object") return old;
            return {
              ...old,
              isActive,
              activatedDate: isActive ? new Date().toISOString() : undefined,
            };
          },
        );
      }

      // Update configuration values in cache
      queryClient.setQueryData(
        moduleConfigurationKeys.value(integrationName, moduleName, context),
        configurations,
      );

      // Update parent integration if this is the last active module being disabled
      // Only update the integration cache that matches the current context (tenant vs client)
      if (shouldDisableParentIntegration) {
        // Build query params based on context to only update matching cache entries
        const queryParams =
          context.level === "client"
            ? { tenantId: context.tenantId, clientId: context.clientId }
            : { tenantId: context.tenantId, clientId: null };

        queryClient.setQueriesData(
          { queryKey: integrationsKeys.list(queryParams) },
          (old: unknown) => {
            if (!old || !Array.isArray(old)) return old;

            return old.map((integration) =>
              integration.name === integrationName
                ? { ...integration, isActive: false, activatedDate: undefined }
                : integration,
            );
          },
        );
      }

      // Show success toast after a brief delay to ensure dialog closes first
      setTimeout(() => {
        showToast("Module configuration updated successfully", "success");
      }, 150);

      // Close dialog on success so it does not stay open (e.g. when disabling Indeed JobSync - OR-1781)
      onSuccessClose?.();
    },
    onSettled: async (_data, _error, variables) => {
      // Invalidate all integration modules queries for this integration name
      // Using prefix match to catch all variations (with/without clientId, null vs undefined, etc.)
      await queryClient.invalidateQueries({
        queryKey: integrationModulesKeys.byIntegration(
          variables.integrationName,
        ),
        refetchType: "all",
      });

      // Invalidate all integration-related queries to ensure consistency
      // This catches all integrations, configurations, and module-specific queries (e.g., job-sync overview)
      // Using refetchType: 'all' ensures queries refetch even if not currently active (e.g., tenant overview when updating client module)
      await queryClient.invalidateQueries({
        queryKey: ["integrations"],
        refetchType: "all", // Refetch all matching queries, including inactive ones
      });

      runModuleToggleHandlers(
        variables.integrationName,
        variables.moduleName,
        queryClient,
      );
    },
  });
}
